/**
 * API 客户端。
 *
 * 所有请求都经过这里，因此三件事只需要写一遍：
 *   - 解开 `{ requestId, data }` 信封
 *   - 附加 Bearer token
 *   - 401 时清掉登录态并跳回登录页
 *
 * 不在页面里手拼 URL（apps/admin/README.md「技术边界」）。
 */

const TOKEN_KEY = "dreamverse.admin.token";

export interface ApiError extends Error {
  code: string;
  status: number;
  requestId: string;
  issues?: Array<{ path: string; message: string }>;
}

export interface Paged<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string | null): void {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

/** 登录失效时由 AuthProvider 注册，避免这里直接依赖 React。 */
let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: (() => void) | null): void {
  onUnauthorized = fn;
}

export type QueryParams = Record<string, string | number | boolean | undefined | null>;

export function buildQuery(params?: QueryParams): string {
  if (!params) return "";
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    usp.set(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : "";
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const token = getToken();
  // 浏览器必须自己给 FormData 生成带 boundary 的 Content-Type。
  // 手填 application/json 会让 Nest/Multer 完全收不到文件。
  const isFormData = typeof FormData !== "undefined" && init?.body instanceof FormData;
  const res = await fetch(path, {
    ...init,
    headers: {
      ...(isFormData ? {} : { "Content-Type": "application/json" }),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init?.headers ?? {}),
    },
  });

  const body = (await res.json().catch(() => null)) as
    | {
        requestId?: string;
        data?: T;
        error?: {
          code?: string;
          message?: string;
          issues?: Array<{ path?: string; message?: string }>;
        };
      }
    | null;

  if (!res.ok || body?.error) {
    if (res.status === 401) {
      setToken(null);
      onUnauthorized?.();
    }
    const issues = body?.error?.issues?.map((issue) => ({
      path: issue.path ?? "",
      message: issue.message ?? "参数不合法",
    }));
    const detail = issues
      ?.map((issue) => `${issue.path || "参数"}：${issue.message}`)
      .join("；");
    const baseMessage = body?.error?.message ?? `请求失败（HTTP ${res.status}）`;
    const err = new Error(detail ? `${baseMessage}：${detail}` : baseMessage) as ApiError;
    err.code = body?.error?.code ?? "HTTP_ERROR";
    err.status = res.status;
    err.requestId = body?.requestId ?? "";
    err.issues = issues;
    throw err;
  }

  return body?.data as T;
}

/** 二进制响应不走 `{ requestId, data }` 信封，用于带管理员鉴权的图片回源。 */
async function requestBlob(path: string): Promise<Blob> {
  const token = getToken();
  const res = await fetch(path, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as
      | { requestId?: string; error?: { code?: string; message?: string } }
      | null;
    if (res.status === 401) {
      setToken(null);
      onUnauthorized?.();
    }
    const err = new Error(body?.error?.message ?? `请求失败（HTTP ${res.status}）`) as ApiError;
    err.code = body?.error?.code ?? "HTTP_ERROR";
    err.status = res.status;
    err.requestId = body?.requestId ?? "";
    throw err;
  }
  return res.blob();
}

export const api = {
  get: <T>(path: string, params?: QueryParams): Promise<T> =>
    request<T>(`${path}${buildQuery(params)}`),
  post: <T>(path: string, payload?: unknown): Promise<T> =>
    request<T>(path, { method: "POST", body: JSON.stringify(payload ?? {}) }),
  postForm: <T>(path: string, payload: FormData): Promise<T> =>
    request<T>(path, { method: "POST", body: payload }),
  blob: (path: string): Promise<Blob> => requestBlob(path),
  patch: <T>(path: string, payload?: unknown): Promise<T> =>
    request<T>(path, { method: "PATCH", body: JSON.stringify(payload ?? {}) }),
  /**
   * DELETE 带 body。
   *
   * 不是常规用法，但删除类操作必须带 `reason` 进审计（见 ADMIN_CONSOLE.md §7），
   * 而把理由塞进 query string 会被日志和浏览器历史原样记录。
   * Express 与 fetch 都支持 DELETE 携带 body。
   */
  del: <T>(path: string, payload?: unknown): Promise<T> =>
    request<T>(path, { method: "DELETE", body: JSON.stringify(payload ?? {}) }),
};

// ---------------------------------------------------------------------------
// 认证

export interface AdminIdentity {
  id: string;
  name: string | null;
  phoneMasked: string | null;
  roles: { key: string; displayName: string }[];
  permissions: string[];
}

export interface LoginResult {
  token: string;
  expiresInSeconds: number;
  admin: AdminIdentity;
}

export const authApi = {
  requestCode: (phone: string) =>
    api.post<{ devCode: string | null; environment: "development" | "production" }>(
      "/api/v1/admin/auth/code",
      { phone },
    ),
  login: (phone: string, code: string) =>
    api.post<LoginResult>("/api/v1/admin/auth/login", { phone, code }),
  me: () => api.get<AdminIdentity>("/api/v1/admin/auth/me"),
};
